import { motion } from "framer-motion";
import { Section } from "../components/Section";
import { SEO } from "../components/SEO";
import { FreeTrialSchedule } from "../components/FreeTrialSchedule";

export function AboutPage() {
  return (
    <>
      <SEO
        title="About Valkyrie Robotics"
        description="Learn about Valkyrie Robotics, a competitive VEX Robotics coaching program in Forsyth County, GA. Our experienced mentors help students develop STEM skills and compete at regional and world-class levels."
        canonical="/about"
        keywords={[
          "about Valkyrie Robotics",
          "VEX Robotics coaching",
          "robotics mentors",
          "competitive robotics program",
          "Forsyth County robotics",
          "STEM robotics coaching",
          "robotics competition training",
          "student robotics development",
        ]}
      />
      <div className="bg-neutral-900">
        <section className="mx-auto max-w-6xl px-4 py-16 text-gray-100">
          <div className="space-y-20">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                About Valkyrie Robotics
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Building the next generation of engineers, coders, and leaders
                through competitive robotics
              </p>
            </div>

            {/* Mission Section */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Image left on large screens */}
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 to-transparent rounded-2xl"></div>
                  <img
                    src="/media/about1.png"
                    alt="Valkyrie Robotics mission — students building and learning together"
                    className="relative w-full h-auto object-cover rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                  />
                </div>
              </div>
              {/* Text right on large screens */}
              <div className="order-1 lg:order-2 space-y-6">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                    Our Mission
                  </h2>
                  <p className="text-xl italic text-brand-400 font-medium mb-6">
                    Inspiring the Next Generation of Engineers, Coders, and
                    Leaders
                  </p>
                </div>
                <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                  <p>
                    At Valkyrie Robotics, we believe that competitive robotics
                    is more than just building robots — it's about building
                    young minds, strong teams, and future‑ready leaders. Through
                    hands‑on coaching, real competition experience, and a love
                    for engineering, we help students discover their potential
                    and push their limits in a fun, supportive environment.
                  </p>
                  <p>
                    We specialize in{" "}
                    <strong className="text-brand-400">VEX IQ</strong> and{" "}
                    <strong className="text-brand-400">VEX V5</strong>{" "}
                    competitive robotics programs for students ages 8–18 in
                    Forsyth County. Our goal is to provide a high‑quality,
                    individualized coaching experience that turns beginners into
                    confident competitors — and champions.
                  </p>
                </div>
              </div>
            </div>

            {/* Program Overview Section */}
            <div className="bg-neutral-800/30 rounded-2xl p-8 border border-neutral-700">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold tracking-tight mb-4">
                  Program Overview
                </h2>
                <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                  Our coaching programs serve students from beginner to
                  championship level, helping them grow skills in engineering,
                  coding, teamwork, and competition strategy.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-neutral-700/50 rounded-xl p-6 border border-neutral-600 hover:border-brand-500/50 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center mr-4">
                      <span className="text-2xl font-bold text-brand-400">
                        IQ
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-100">
                        VEX IQ Coaching
                      </h3>
                      <p className="text-brand-400 font-medium">Ages 8–14</p>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Introductory and intermediate robotics for elementary and
                    middle school students. We focus on structure, sensors,
                    coding (blocks or Python), and developing a
                    competition‑ready mindset.
                  </p>
                </div>

                <div className="bg-neutral-700/50 rounded-xl p-6 border border-neutral-600 hover:border-brand-500/50 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center mr-4">
                      <span className="text-2xl font-bold text-brand-400">
                        V5
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-100">
                        VEX V5 Coaching
                      </h3>
                      <p className="text-brand-400 font-medium">Ages 12–18</p>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Advanced robotics for middle and high school students.
                    Students learn high‑level robot design, coding in
                    VEXcode/PROS, and notebook development — pushing toward
                    State and World‑level performance.
                  </p>
                </div>
              </div>

              <div className="text-center mt-8">
                <p className="text-gray-300 mb-4">Not sure where to start?</p>
                <a
                  className="inline-flex items-center text-brand-400 hover:text-brand-300 font-medium transition-colors"
                  href="/blog/which-program-is-right-for-my-child"
                >
                  Read our guide: VEX IQ vs. V5 – Which program is right for my
                  child?
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </div>
            </div>

            {/* Coach Section */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Text left on large screens */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                    Meet Your Head Coach
                  </h2>
                  <p className="text-2xl text-brand-400 font-semibold">
                    Andriy Plakosh
                  </p>
                </div>

                <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                  <p>
                    From a young age, Andriy Plakosh has had a passion for
                    understanding how things work. At six he began building
                    circuit diagrams; by third grade he was repairing computers
                    and small appliances — a curiosity and problem‑solving
                    mindset that continues today.
                  </p>
                  <p>
                    Andriy brings years of hands‑on experience in competitive
                    VEX IQ and VEX V5 robotics, leading teams to
                    <strong className="text-brand-400"> State</strong> and{" "}
                    <strong className="text-brand-400">
                      {" "}
                      World Championship
                    </strong>{" "}
                    levels. As an engineer focused on structure, precision, and
                    reliability, he has personally built nearly ten world‑class
                    robots, contributed extensively to engineering notebooks,
                    and developed coding solutions for autonomous and
                    driver‑controlled routines.
                  </p>
                  <p>
                    In 2024–2025, Andriy's VEX V5 team qualified for the Georgia
                    State Championship and placed
                    <strong className="text-brand-400">
                      {" "}
                      1st in Robotics
                    </strong>{" "}
                    at the TSA State Leadership Conference, and will represent
                    Georgia at the National Leadership Conference in Nashville,
                    TN.
                  </p>
                  <p>
                    Beyond competition, Andriy volunteers as a VEX IQ referee in
                    Forsyth County and is working toward head referee
                    certification. He also mentors students at Dave's Creek
                    Elementary, sharing best practices in coding and robot
                    engineering.
                  </p>
                </div>
              </div>

              {/* Image right on large screens */}
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 to-transparent rounded-2xl"></div>
                  <img
                    src="/media/about2.png"
                    alt="Head coach Andriy Plakosh mentoring students"
                    className="relative w-full h-auto object-cover rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                  />
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-gradient-to-r from-neutral-800/50 to-neutral-700/50 rounded-2xl p-12 border border-neutral-600">
              <h2 className="text-3xl font-bold tracking-tight mb-4">
                Ready to Join the Team?
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Think your child would thrive in robotics? Let's talk about how
                we can help them discover their potential!
              </p>
              <div className="flex flex-col items-center gap-4">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    className="rounded-lg bg-brand-600 text-neutral-900 font-semibold px-8 py-4 text-lg transition-all duration-150 ease-out hover:scale-105 active:scale-95 hover:bg-brand-500 shadow-lg"
                    data-cal-namespace="free-trial"
                    data-cal-link="aplakosh/free-trial"
                    data-cal-config='{"layout":"month_view"}'
                  >
                    Book Free Trial Session
                  </button>
                  <a
                    href="/contact"
                    className="text-brand-400 hover:text-brand-300 font-medium transition-colors"
                  >
                    Or send us a message →
                  </a>
                </div>
                <FreeTrialSchedule />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
